import { ExternalCustomerIntegration, IntegrationProviders, NewCustomer, UpdateCustomer } from '@vy/types';
import { isEmptyString, isNullOrUndefined, Optional } from '@vy/utils';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { AutocompleteInputChangeReason } from '@mui/material';
import { DEBOUNCE_DELAY } from '../../../../components/design/Input/input-consts';
import { useDebounce } from '../../../../hooks/use-debounce';
import { ExternalCustomer, ExternalCustomerSelectionConfig } from '../customers-component-types';

export function useExternalCustomerSelection({
  provider,
  useCustomersHook,
}: ExternalCustomerSelectionConfig) {
  const [searchInput, setSearchValue] = useState<string | undefined>(undefined);
  const [selectedCustomer, setSelectedCustomer] = useState<ExternalCustomer | null>(null);
  const debouncedSearchValue = useDebounce(searchInput, DEBOUNCE_DELAY);
  const { setValue, getValues, watch } = useFormContext<NewCustomer | UpdateCustomer>();

  const { data: { customers = [] } = {}, isFetching } = useCustomersHook(debouncedSearchValue, {
    keepPreviousData: true,
  });

  const externalIntegration = watch('externalIntegration');

  const currentCustomer = useMemo(() => {
    const integration = externalIntegration?.find(
      (externalIntegration) => externalIntegration.type === provider,
    );

    if (!isMatchingIntegration(integration, provider)) {
      return null;
    }

    return {
      id: integration.id,
      name: integration.name,
    };
  }, [externalIntegration, customers, provider]);

  useEffect(() => {
    if (currentCustomer && !selectedCustomer) {
      setSelectedCustomer(currentCustomer);
    }
  }, [currentCustomer, selectedCustomer]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmptyString(event.target.value)) {
      const currentExternalIntegration = getValues('externalIntegration');
      const externalIntegrationsWithoutProvider = currentExternalIntegration?.filter(
        (integration) => integration.type !== provider,
      );
      setValue('externalIntegration', externalIntegrationsWithoutProvider, { shouldDirty: true });
      setSelectedCustomer(null);
    }

    setSearchValue(event.target.value);
  };

  // Use with MUI Autocomplete's onInputChange. Skips reason="reset" (fired after selection
  // with the selected label) so we don't search the label as a new query.
  const handleInputChange = (_: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') return;

    if (isEmptyString(value)) {
      const currentExternalIntegration = getValues('externalIntegration');
      const externalIntegrationsWithoutProvider = currentExternalIntegration?.filter(
        (integration) => integration.type !== provider,
      );
      setValue('externalIntegration', externalIntegrationsWithoutProvider, { shouldDirty: true });
      setSelectedCustomer(null);
    }

    setSearchValue(value || undefined);
  };

  const handleCustomerChange = (_: React.SyntheticEvent, value: ExternalCustomer | null) => {
    setSelectedCustomer(value);

    const currentExternalIntegration = getValues('externalIntegration');
    const externalIntegrationsWithoutProvider = currentExternalIntegration?.filter(
      (integration) => integration.type !== provider,
    );

    if (isNullOrUndefined(value)) {
      return setValue('externalIntegration', externalIntegrationsWithoutProvider, {
        shouldDirty: true,
      });
    }

    setValue(
      'externalIntegration',
      [
        ...(externalIntegrationsWithoutProvider ?? []),
        {
          type: provider,
          id: value.id,
          name: value.name,
        },
      ],
      { shouldDirty: true },
    );
  };

  return {
    selectedCustomer,
    searchInput,
    customers,
    isFetching,
    handleSearchChange,
    handleInputChange,
    handleCustomerChange,
  };
}

function isMatchingIntegration(
  externalIntegration: Optional<ExternalCustomerIntegration>,
  provider: IntegrationProviders,
): externalIntegration is ExternalCustomerIntegration {
  return externalIntegration?.type === provider;
}
